/**
 * ProteinCrystalSystem — jagged proteinaceous shards scattered across
 * the dish that damage the player on contact.
 *
 * Density is biomass-driven: zero below 10K, ramping linearly up to a
 * cap at the same 125K saturation point the bioturbidity uses, so the
 * dish gets visibly more hostile as the cell engorges. Crystals spawn
 * at random positions away from the player, drift slowly, and shatter
 * on contact (small particle burst + flat damage).
 *
 * Self-contained: doesn't use the ECS query path because the entities
 * never interact with food/predator logic. Lives on its own Container
 * inside the world layer so the camera shake / zoom apply uniformly.
 */

import { Container, Graphics } from 'pixi.js';
import { bus } from '@/core/EventBus';
import { player } from './PlayerState';

interface Crystal {
  g: Graphics;
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  rotVel: number;
  size: number;
  age: number;
  /** spawning: fading in. alive: collidable. dying: shattering, no damage. */
  state: 'spawning' | 'alive' | 'dying';
}

const SPAWN_FADE = 0.6;
const DIE_FADE = 0.35;
const BIOMASS_START = 1_000;
const BIOMASS_FULL = 250_000;
const TARGET_MIN = 8;  // floor: a fuller scatter from the moment the threshold trips
const TARGET_MAX = 28; // saturation: a hostile dish at the stage target
const CONTACT_DAMAGE = 10;
const PLAYER_SPAWN_CLEAR = 180;
const SPAWN_INTERVAL = 0.18; // fast initial fill so the floor populates quickly

export class ProteinCrystalSystem {
  private layer: Container;
  private crystals: Crystal[] = [];
  private intensity = 0;
  private spawnAccum = 0;

  constructor(worldLayer: Container) {
    this.layer = new Container();
    // Add at the same depth as turbidity floaters so crystals feel like
    // part of the environment, not above-foreground UI.
    worldLayer.addChildAt(this.layer, 0);
  }

  /** 0..1 — multiplies the target population. Stages compute this from
   *  biomass; passing 0 phases the system out. */
  setIntensity(v: number): void {
    this.intensity = Math.max(0, Math.min(1, v));
  }

  /** Convenience: stages can pass biomass directly. */
  setFromBiomass(biomass: number): void {
    if (biomass < BIOMASS_START) { this.setIntensity(0); return; }
    this.setIntensity(Math.min(1, (biomass - BIOMASS_START) / (BIOMASS_FULL - BIOMASS_START)));
  }

  update(dt: number, screenW: number, screenH: number, px: number, py: number, playerR: number): void {
    // ---- spawn pacing ----
    // Floor at TARGET_MIN once intensity > 0 so the threshold trip drops
    // a small scattered field immediately, then escalates linearly up to
    // TARGET_MAX as biomass climbs. Without the floor, round() takes the
    // first several thousand biomass before any shard appears at all.
    const target = this.intensity > 0
      ? Math.max(TARGET_MIN, Math.round(this.intensity * TARGET_MAX))
      : 0;
    this.spawnAccum += dt;
    while (this.aliveCount() < target && this.spawnAccum >= SPAWN_INTERVAL) {
      this.spawnAccum -= SPAWN_INTERVAL;
      this.spawnOne(screenW, screenH, px, py);
    }

    // ---- per-crystal step ----
    for (let i = this.crystals.length - 1; i >= 0; i--) {
      const c = this.crystals[i];
      c.age += dt;
      c.x += c.vx * dt;
      c.y += c.vy * dt;
      c.rot += c.rotVel * dt;
      c.g.position.set(c.x, c.y);
      c.g.rotation = c.rot;

      if (c.state === 'spawning') {
        const t = Math.min(1, c.age / SPAWN_FADE);
        c.g.alpha = t;
        c.g.scale.set(0.4 + t * 0.6);
        if (t >= 1) { c.state = 'alive'; c.age = 0; }
      } else if (c.state === 'alive') {
        // contact check — circle vs diamond is fine as a circle here
        const dx = px - c.x, dy = py - c.y;
        const r = playerR + c.size * 0.7;
        if (dx * dx + dy * dy < r * r) {
          this.shatter(c);
          continue;
        }
        // Drift off screen → recycle so we keep the field roaming.
        if (c.x < -80 || c.x > screenW + 80 || c.y < -80 || c.y > screenH + 80) {
          this.disposeAt(i);
          continue;
        }
      } else { // dying
        const t = Math.min(1, c.age / DIE_FADE);
        c.g.alpha = (1 - t) * 0.85;
        c.g.scale.set(1 + t * 1.4);
        if (t >= 1) {
          this.disposeAt(i);
          continue;
        }
      }
    }
  }

  /** Clear the field without tearing down the layer — used on restart so
   *  the player doesn't inherit shards from the previous run. */
  reset(): void {
    for (const c of this.crystals) c.g.destroy();
    this.crystals.length = 0;
    this.spawnAccum = 0;
    this.intensity = 0;
  }

  destroy(): void {
    for (const c of this.crystals) c.g.destroy();
    this.crystals.length = 0;
    this.layer.destroy({ children: true });
  }

  // ---- internals ----

  private aliveCount(): number {
    let n = 0;
    for (const c of this.crystals) if (c.state !== 'dying') n++;
    return n;
  }

  private spawnOne(w: number, h: number, px: number, py: number): void {
    let x = 0, y = 0;
    // Avoid placing right on the player. A few attempts; if we can't find
    // a clear spot, just place anyway so density still tracks intensity.
    for (let i = 0; i < 6; i++) {
      x = 40 + Math.random() * (w - 80);
      y = 40 + Math.random() * (h - 80);
      const dx = x - px, dy = y - py;
      if (dx * dx + dy * dy > PLAYER_SPAWN_CLEAR * PLAYER_SPAWN_CLEAR) break;
    }
    const size = 9 + Math.random() * 7;
    const g = new Graphics();
    drawCrystal(g, size);
    g.position.set(x, y);
    g.rotation = Math.random() * Math.PI;
    g.alpha = 0;
    g.scale.set(0.4);
    this.layer.addChild(g);

    const angle = Math.random() * Math.PI * 2;
    const speed = 6 + Math.random() * 10;
    this.crystals.push({
      g, x, y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      rot: g.rotation,
      rotVel: (Math.random() - 0.5) * 0.6,
      size,
      age: 0,
      state: 'spawning',
    });
  }

  private shatter(c: Crystal): void {
    c.state = 'dying';
    c.age = 0;
    c.vx *= 0.3;
    c.vy *= 0.3;
    player.damage(CONTACT_DAMAGE, 'protein_crystal');
    bus.emit('fx:particles', { x: c.x, y: c.y, count: 14, color: 0xa5d8e6, spread: 90 });
    bus.emit('fx:screenShake', { intensity: 6, duration: 0.22 });
    bus.emit('fx:flash', { color: 0xa5d8e6, alpha: 0.18, duration: 0.18 });
    bus.emit('fx:floatingText', { x: c.x, y: c.y - 14, text: `-${CONTACT_DAMAGE}`, color: 0xa5d8e6 });
    bus.emit('audio:cue', { id: 'damage', volume: 0.6 });
  }

  private disposeAt(i: number): void {
    const c = this.crystals[i];
    c.g.destroy();
    this.crystals.splice(i, 1);
  }
}

function drawCrystal(g: Graphics, size: number): void {
  // Diamond-ish 6-point shard with a brighter inner core. Slight
  // random jitter on the outer points so each crystal looks a bit
  // different rather than stamped from a template.
  const points: number[] = [];
  const sides = 6;
  for (let i = 0; i < sides; i++) {
    const a = (i / sides) * Math.PI * 2;
    const r = size * (0.85 + Math.random() * 0.3);
    points.push(Math.cos(a) * r, Math.sin(a) * r);
  }
  g.poly(points).fill({ color: 0x87b8c5, alpha: 0.28 });
  g.poly(points).stroke({ color: 0xc8e7f0, width: 1.4, alpha: 0.85 });

  // inner highlight
  const inner: number[] = [];
  for (let i = 0; i < sides; i++) {
    const a = (i / sides) * Math.PI * 2 + 0.3;
    const r = size * 0.4;
    inner.push(Math.cos(a) * r, Math.sin(a) * r);
  }
  g.poly(inner).fill({ color: 0xe6f4f8, alpha: 0.55 });
}
